import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import { AgentAssignmentPoolOverview, LoadingState, Configuration, UpdateAgentAssignmentPoolRequest } from '@types'

interface configurationsState {
  loadingStatus: LoadingState
  message: string
  configurations: Configuration[]
  poolOverview?: AgentAssignmentPoolOverview
  poolLoadingStatus: LoadingState
  poolUpdateStatus: LoadingState
  poolMessage: string
}

export const initialState: configurationsState = {
  loadingStatus: 'NOT_STARTED',
  message: '',
  configurations: [],
  poolOverview: undefined,
  poolLoadingStatus: 'NOT_STARTED',
  poolUpdateStatus: 'NOT_STARTED',
  poolMessage: ''
}

export type UpdateConfigurationAction = PayloadAction<Partial<Configuration>>
export type UpdateAgentAssignmentPoolAction = PayloadAction<UpdateAgentAssignmentPoolRequest>

const resourceSlice = createSlice({
  name: 'configurations',
  initialState,

  reducers: {
    getConfigurations: state => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getConfigurationsSuccess: (state, action: PayloadAction<Configuration[]>) => {
      return {
        ...state,
        loadingStatus: 'SUCCESS',
        configurations: action.payload
      }
    },
    getConfigurationsFailed: (state, action) => {
      return {
        ...state,
        loadingStatus: 'FAILED',
        message: action.payload
      }
    },
    updateConfiguration: (state, _action: UpdateConfigurationAction) => {
      return {
        ...state,
        loadingStatus: 'LOADING'
      }
    },
    getAgentAssignmentPool: state => {
      state.poolLoadingStatus = 'LOADING'
      state.poolMessage = ''
    },
    getAgentAssignmentPoolSuccess: (state, action: PayloadAction<AgentAssignmentPoolOverview>) => {
      state.poolLoadingStatus = 'SUCCESS'
      state.poolOverview = action.payload
      state.poolMessage = ''
    },
    getAgentAssignmentPoolFailed: (state, action: PayloadAction<string>) => {
      state.poolLoadingStatus = 'FAILED'
      state.poolMessage = action.payload
    },
    updateAgentAssignmentPool: (state, _action: UpdateAgentAssignmentPoolAction) => {
      state.poolUpdateStatus = 'LOADING'
      state.poolMessage = ''
    },
    updateAgentAssignmentPoolSuccess: (state, action: PayloadAction<AgentAssignmentPoolOverview>) => {
      state.poolUpdateStatus = 'SUCCESS'
      state.poolOverview = action.payload
      state.poolMessage = ''
    },
    updateAgentAssignmentPoolFailed: (state, action: PayloadAction<string>) => {
      state.poolUpdateStatus = 'FAILED'
      state.poolMessage = action.payload
    },
    clearConfigurations: _state => {
      return initialState
    }
  }
})

export const configurationsLoader = createDraftSafeSelector(
  [(state: GlobalState) => state.configurations.loadingStatus, (state: GlobalState) => state.configurations.message],
  (loadingStatus, message) => {
    const isLoading = loadingStatus === 'LOADING'
    const isFailed = loadingStatus === 'FAILED'
    const isSuccess = loadingStatus === 'SUCCESS'
    return { isLoading, isFailed, isSuccess, message }
  }
)

export const selectConfigurations = createDraftSafeSelector(
  [(state: GlobalState) => state.configurations.configurations],
  configurations => configurations
)

export const selectAgentAssignmentPoolState = createDraftSafeSelector(
  [
    (state: GlobalState) => state.configurations.poolOverview,
    (state: GlobalState) => state.configurations.poolLoadingStatus,
    (state: GlobalState) => state.configurations.poolUpdateStatus,
    (state: GlobalState) => state.configurations.poolMessage
  ],
  (overview, loadingStatus, updateStatus, message) => ({
    overview,
    isLoading: loadingStatus === 'LOADING',
    isUpdating: updateStatus === 'LOADING',
    error: loadingStatus === 'FAILED' || updateStatus === 'FAILED' ? message : ''
  })
)

export const {
  getAgentAssignmentPool,
  getAgentAssignmentPoolFailed,
  getAgentAssignmentPoolSuccess,
  getConfigurations,
  getConfigurationsFailed,
  getConfigurationsSuccess,
  updateAgentAssignmentPool,
  updateAgentAssignmentPoolFailed,
  updateAgentAssignmentPoolSuccess,
  updateConfiguration
} = resourceSlice.actions
export default resourceSlice.reducer
